import React from 'react';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ResourceAccessEntry } from '@cotera/client/app/etc/api/typed-client';
import { useOrgMembersById } from './use-org-members';
import { MemberAvatar } from './member-avatar';

/** How many avatar circles to show before collapsing the rest into "+N". */
const MAX_AVATARS = 3;

/** One person a resource is shared with, named however we can name them. */
export type Grantee = { id: string; label: string | null };

/**
 * The people a resource is explicitly shared with, owner excluded.
 *
 * Three steps that were written twice, differently, and have to agree: keep only
 * user grants (an `org` entry is "everyone", not a person), drop the owner (who
 * is not someone it was *shared* with), and name each one — trimmed display name
 * if there is one, email if not, `null` if they are outside the org or the member
 * list hasn't loaded. `MemberAvatar` renders `?` for that last case.
 */
export function useGrantees(
  users: readonly ResourceAccessEntry[] | undefined,
  ownerId: string | null | undefined
): Grantee[] {
  const membersById = useOrgMembersById();
  return (users ?? []).flatMap((entry) => {
    if (entry.t !== 'user' || entry.id === ownerId) {
      return [];
    }
    const member = membersById.get(entry.id);
    const name = member?.name?.trim();
    return [
      {
        id: entry.id,
        label: name !== undefined && name !== '' ? name : member?.email ?? null,
      },
    ];
  });
}

/**
 * Overlapping initial-circle stack for the people a resource is shared with.
 *
 * The negative gap is the whole idea — overlapping circles read as "a group"
 * where a spaced row reads as a list, and the cap plus `+N` keeps the width
 * bounded however many people a resource is shared with.
 *
 * `avatarClassName` / `overflowClassName` exist because the two surfaces this
 * renders on disagree only about trim: a Library row sizes the count to its
 * column's text, and the chat header rings each circle in the header's own
 * background so the overlap reads on a busy bar. Everything structural is shared.
 */
export const GranteeAvatarStack: React.FC<{
  people: Grantee[];
  avatarClassName?: string;
  overflowClassName?: string;
}> = ({ people, avatarClassName, overflowClassName }) => {
  const shown = people.slice(0, MAX_AVATARS);
  const overflow = people.length - shown.length;
  return (
    <div className="flex items-center">
      <div className="flex items-center -space-x-1.5">
        {shown.map((p) => (
          <MemberAvatar
            key={p.id}
            seed={p.id}
            label={p.label}
            className={avatarClassName}
          />
        ))}
      </div>
      {overflow > 0 ? (
        <span
          className={cn('ml-1 font-medium tabular-nums', overflowClassName)}
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  );
};
